
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Shield, Globe, TrendingUp, DollarSign, Users, BarChart as BarChartIcon, Megaphone } from "lucide-react";
import {SEO} from "@/components/SEO.tsx";
import React from "react";

const growthData = [
  { month: 'Jan', users: 1000 },
  { month: 'Feb', users: 2500 },
  { month: 'Mar', users: 4000 },
  { month: 'Apr', users: 7000 },
  { month: 'May', users: 12000 },
  { month: 'Jun', users: 18000 },
];

const roiData = [
  { category: 'Traditional', value: 120 },
  { category: 'Social Media', value: 150 },
  { category: 'blankpoll', value: 280 },
];

const Insights = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
          title="Insights"
          description="Analyze poll trends, user behavior, and platform stats"
          type="website"
          name="blankpoll"
          imageUrl="/blankpoll-banner.jpg"
      />
      <Navigation />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-6">
            Transform Your Market Research
          </h1>
          <p className="text-xl text-center text-secondary-light max-w-2xl mx-auto">
            Get actionable insights from verified users worldwide and boost your brand's presence with our innovative polling platform.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-16 space-y-24">
        {/* Verified Users Section */}
        <section className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">Verified Users Network</h2>
            </div>
            <p className="text-secondary-light mb-6">
              Access our network of verified users who provide genuine engagement and valuable feedback. Our verification process ensures high-quality responses and meaningful insights for your brand.
            </p>
            <div className="bg-accent rounded-lg p-6">
              <h4 className="font-semibold mb-2">Key Statistics:</h4>
              <ul className="list-disc list-inside text-secondary-light">
                <li>95% user verification rate</li>
                <li>Average engagement rate of 78%</li>
                <li>Verified active users</li>
              </ul>
            </div>
          </div>
          <Card className="p-6">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="users" stroke="#A7B49E" name="Verified Users Growth" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </section>

        {/* Brand Awareness Section */}
        <section className="grid md:grid-cols-2 gap-12 items-center">
          <Card className="p-6">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={roiData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#A7B49E" name="ROI %" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">Brand Awareness & ROI</h2>
            </div>
            <p className="text-secondary-light mb-6">
              Our platform delivers exceptional ROI through targeted engagement and valuable market insights. Companies using blankpoll see an average increase of 280% in engagement compared to traditional methods.
            </p>
            <div className="bg-accent rounded-lg p-6">
              <h4 className="font-semibold mb-2">Achievement Highlights:</h4>
              <ul className="list-disc list-inside text-secondary-light">
                <li>2.8x higher ROI than traditional surveys</li>
                <li>45% increase in brand visibility</li>
                <li>68% more user engagement</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="mt-16">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose blankpoll?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-6">
              <Globe className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">Global Market Analysis</h3>
              <p className="text-secondary-light">
                Access insights and reach audiences across 190+ countries. Get real-time feedback from diverse demographics worldwide.
              </p>
            </Card>

            <Card className="p-6">
              <Users className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">Multi-Channel Reach</h3>
              <p className="text-secondary-light">
                Connect with millions of users across various platforms and demographics for comprehensive market coverage.
              </p>
            </Card>

            <Card className="p-6">
              <Shield className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">Secure & Reliable Data</h3>
              <p className="text-secondary-light">
                Get accurate and reliable market insights with our secure polling system and advanced analytics.
              </p>
            </Card>

            <Card className="p-6">
              <TrendingUp className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">Enhanced Marketing Impact</h3>
              <p className="text-secondary-light">
                Leverage poll results for targeted marketing campaigns and achieve better ROI through data-driven decisions.
              </p>
            </Card>

            <Card className="p-6">
              <BarChartIcon className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">Real-time Analytics</h3>
              <p className="text-secondary-light">
                Monitor poll performance and user engagement in real-time with our advanced analytics dashboard.
              </p>
            </Card>

            <Card className="p-6">
              <Megaphone className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">PR & Media Coverage</h3>
              <p className="text-secondary-light">
                Generate newsworthy insights and increase media coverage through engaging polls and surveys.
              </p>
            </Card>
          </div>
        </section>

        {/* Presentation for Business Section */}
        <section className="mt-16">
          <h2 className="text-3xl font-bold text-center mb-8">Presentation for Business</h2>
          <div className="w-full rounded-[5px] overflow-hidden relative" style={{ paddingTop: '56.25%' }}>
            <iframe
              loading="lazy"
              className="absolute inset-0 w-full h-full border-none p-0 m-0 rounded-[5px]"
              src="https://www.canva.com/design/DAGgGa0JsfI/0AfiLuQG8Q4w_pM29NzRKw/view?embed"
              allowFullScreen
              allow="fullscreen"
            ></iframe>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default Insights;
